import type { RentalOverdueClockView } from './rentalClock';
import type { ApiRentalDealItem, ApiRentalContractState } from 'src/types/rental';

import { RentalDealStatus } from 'src/types/rental';

import { overdueClockView } from './rentalClock';

/**
 * Which renter action is available on a rental, and what each one calls (contracts v3, UI §5).
 *
 * Pure, and split out of the screen for the reason every rule here exists: each line mirrors a
 * guard inside `RentalDeal.sol`, and an offered button whose transaction reverts costs the renter
 * a network fee to be told "nothing happened". Unit tests hold the mirror steady — the screen
 * cannot be tested (no RTL in this app), and these are the rules worth testing anyway.
 *
 * Two generations live behind the same screen ([[one-abi-decodes-all-contract-generations]]):
 * TRON deals up to `contractVersion` 3, where a disputed deposit is ruled on by the platform
 * alone, and the EVM generation 4, where it is settled by two matching proposals. The dispute
 * half of the screen therefore asks the contract's generation, not the chain — the deal knows
 * which functions it has, and a `proposeSplit` sent to a v3 clone would revert.
 */

/** The EVM generation: `proposeSplit` / `claimStaleDeposit` / `getDispute` exist from here on. */
export const PROPOSAL_CONTRACT_VERSION = 4;

/** Terminal statuses — nothing left for anyone to sign. */
export const RENTAL_TERMINAL_STATUSES: RentalDealStatus[] = [
  RentalDealStatus.COMPLETED,
  RentalDealStatus.CANCELLED,
];

/** A booking that has not been paid yet: the platform cron frees its dates 30 minutes after Book. */
export const UNPAID_BOOKING_STATUSES: RentalDealStatus[] = [
  RentalDealStatus.CREATED,
  RentalDealStatus.DEPLOY_INITIATED,
  RentalDealStatus.DEPLOY_CONFIRMED,
  RentalDealStatus.DEPLOY_FAILED,
  RentalDealStatus.INITIALIZED,
];

/** Deploy-on-Pay: statuses in which "Pay" also means "deploy the escrow first" (v2.1). */
const AWAITING_DEPLOY_STATUSES: RentalDealStatus[] = [
  RentalDealStatus.CREATED,
  RentalDealStatus.DEPLOY_INITIATED,
  RentalDealStatus.DEPLOY_FAILED,
  // The contract is recorded but its state not yet mirrored (the poller's first sync is seconds
  // away): a renter whose creation was just reported, or who left the dialog at the approve, must
  // still see Pay — the dialog re-asks the api and continues from the address (api#380).
  RentalDealStatus.DEPLOY_CONFIRMED,
];

/** CC3: the deposit is locked for a 7-day inspection window after `returnVehicle()`. */
export const INSPECTION_LOCK_SECONDS = 7 * 24 * 60 * 60;

export const isRentalTerminal = (status: RentalDealStatus): boolean =>
  RENTAL_TERMINAL_STATUSES.includes(status);

/** True once the contract carries the v3 dispute mechanics (proposals instead of an arbiter). */
export const supportsSplitProposals = (state: ApiRentalContractState | null | undefined): boolean =>
  (state?.contractVersion ?? 1) >= PROPOSAL_CONTRACT_VERSION;

export interface RentalActionVisibility {
  /** The clock every plate and every amount on the screen reads — computed once, here. */
  clock: RentalOverdueClockView;
  /** Nothing left to do. */
  isTerminal: boolean;
  /** The escrow has not been deployed yet: "Pay" starts (or retries) the deploy first. */
  awaitingDeploy: boolean;
  /** `pay()` — the booking is initialised, unpaid, and its pay deadline has not passed. */
  canPay: boolean;
  /** The contract's own `payDeadline` has passed: `pay()` would revert (v2.2 deals only). */
  payDeadlinePassed: boolean;
  /** `startRental()` — the car is ready and the booked range has not run out. */
  canStart: boolean;
  /** The booked range ended before the car was accepted: `startRental()` would revert (v2.2). */
  bookingExpired: boolean;
  /** `cancel()` — paid, never accepted: rental + deposit come back, the fee stays forfeited. */
  canCancel: boolean;
  /** `returnVehicle()` — the rental is running. */
  canReturn: boolean;
  /** `topUpDeposit()` — there is something to settle or refill. */
  canTopUp: boolean;
  /** `requestExtension()` — running, with no request already pending (CC4 holds one at a time). */
  canRequestExtension: boolean;
  /** A request is waiting for the landlord's answer. */
  extensionPending: boolean;
  /** Past the end date the landlord cannot approve until the deposit is whole and nothing is owed. */
  mustTopUpBeforeExtending: boolean;
  /** The 7-day inspection window is running (no defect, no dispute). */
  inInspection: boolean;
  /** Seconds until `releaseDeposit()` opens; negative once it has. */
  secondsToUnlock: number;
  /** `releaseDeposit()` — the inspection window is over. */
  canRelease: boolean;
  /** The landlord claimed a defect and the renter has to answer it. */
  defectPending: boolean;
  /** The deposit is in dispute. */
  isDisputed: boolean;
}

/**
 * Every renter action of one rental, as of `nowSeconds`.
 *
 * Time is a parameter, not `Date.now()`: two of the rules are deadlines the contract enforces
 * (`payDeadline`, `bookedEnd`) and three more are countdowns, so the whole thing has to be
 * reproducible in a test without freezing the clock.
 */
export const rentalActionVisibility = (
  rental: ApiRentalDealItem,
  nowSeconds: number
): RentalActionVisibility => {
  const state = rental.rentalContractState ?? null;
  const clock = overdueClockView(state, nowSeconds);
  const isTerminal = isRentalTerminal(rental.status);

  // Deploy-on-Pay (v2.1): a fresh booking has no contract yet — confirming in the pay modal asks
  // api to deploy the clone, so the button must show while the deal is still in that lifecycle
  // (deploy_failed included: prepare-payment retries under the api's own attempt ledger).
  const awaitingDeploy = !state && AWAITING_DEPLOY_STATUSES.includes(rental.status);
  // v2.2 writes a hard deadline into the contract; `pay()` reverts past it. Older generations
  // carry none, and there the platform cron is the only thing that ends an unpaid booking.
  const payDeadline = Number(state?.payDeadline ?? 0);
  const payDeadlinePassed = payDeadline > 0 && nowSeconds > payDeadline;
  const canPay =
    !payDeadlinePassed &&
    (awaitingDeploy || (!!state && state.isInitialized && !state.isPaid && !state.isCompleted));

  // `startRental()` refuses acceptance after the booked range: the rental clock runs from
  // acceptance, and a late one would push the rental past the days the car was actually booked.
  const bookedEnd = Number(state?.bookedEnd ?? 0);
  // Only a **paid** booking can expire this way. An unpaid one dies at its pay deadline half an
  // hour after Book, and telling that renter the booked range ran out — next to a plate about the
  // money coming back — promises a refund of a payment that never happened.
  const bookingExpired =
    bookedEnd > 0 && nowSeconds > bookedEnd && !!state && state.isPaid && !state.isActive && !state.isCompleted;
  const readyToStart = !!state && state.isReadyForPickup && !state.isActive && !state.isCompleted;

  const canReturn =
    !!state && state.isActive && !state.isReturned && !state.isInspection && !state.isCompleted;
  const extensionPending = canReturn && state!.pendingExtensionPeriod !== '0';
  const inInspection =
    !!state && state.isInspection && !state.isDefected && !state.isDisputed && !state.isCompleted;
  const secondsToUnlock = state ? Number(state.inspectionStart) + INSPECTION_LOCK_SECONDS - nowSeconds : 0;

  return {
    clock,
    isTerminal,
    awaitingDeploy,
    canPay,
    payDeadlinePassed,
    canStart: readyToStart && !bookingExpired,
    bookingExpired,
    // The contract's guard is `!isPaid || isActive || isCompleted → revert`: paid, not yet
    // accepted, not finished. That is the whole window in which a renter may walk away.
    canCancel: !!state && state.isPaid && !state.isActive && !state.isCompleted,
    canReturn,
    canTopUp: canReturn && clock.isDailyModel && clock.topUpAmount > 0n,
    canRequestExtension: canReturn && !extensionPending,
    extensionPending,
    mustTopUpBeforeExtending: canReturn && clock.needsTopUpForExtension,
    inInspection,
    secondsToUnlock,
    canRelease: inInspection && secondsToUnlock <= 0,
    defectPending: !!state && state.isDefected && !state.isDisputed && !state.isCompleted,
    isDisputed: !!state && state.isDisputed && !state.isCompleted,
  };
};

/** One party's position in a deposit dispute. `null` amount = has not proposed at all. */
export interface SplitProposal {
  party: 'you' | 'dealer' | 'platform';
  label: string;
  /** What this party says the landlord keeps, in base units. */
  rentierAmount: bigint | null;
}

export interface RentalDisputeView {
  /** The deposit is in dispute — show the panel at all. */
  isDisputed: boolean;
  /**
   * The contract settles by proposals (generation 4). A v3 TRON deal is disputed the old way —
   * the platform rules on it — so the panel says that instead of offering buttons that revert.
   */
  supportsProposals: boolean;
  /** What is left to split, in base units. */
  remainingDeposit: bigint;
  proposals: SplitProposal[];
  /** `proposeSplit()` — the renter may publish or re-publish while nothing has executed. */
  canPropose: boolean;
  /**
   * Amounts already on the table that the renter can simply agree to: proposing the same number
   * is what executes the split. The renter's own proposal is not among them.
   */
  agreeable: { party: 'dealer' | 'platform'; label: string; rentierAmount: bigint }[];
  /** `claimStaleDeposit()` — 14 days of dispute nobody settled, and the payer takes the rest. */
  canClaimStale: boolean;
  /** ISO moment the stale claim opens, for the "available from …" line while it is shut. */
  staleClaimAvailableAt: string | null;
}

const toBigOrNull = (value: string | null | undefined): bigint | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  try {
    return BigInt(value);
  } catch {
    return null;
  }
};

/**
 * The deposit dispute as the renter sees it (contracts v3, onchain §3.2).
 *
 * A proposal is what the **landlord** keeps; the renter gets the remainder. Zero is a real
 * proposal ("the landlord keeps nothing"), which is why the wire carries `null` for "has not
 * proposed" and why nothing here treats a missing value as a zero.
 */
export const rentalDisputeView = (
  rental: ApiRentalDealItem,
  nowMs: number = Date.now()
): RentalDisputeView => {
  const state = rental.rentalContractState ?? null;
  const isDisputed = !!state && state.isDisputed && !state.isCompleted;
  const supportsProposals = supportsSplitProposals(state);
  const availableAt = rental.staleClaimAvailableAt ?? null;

  const proposals: SplitProposal[] = [
    { party: 'you', label: 'You', rentierAmount: toBigOrNull(state?.renterProposal) },
    { party: 'dealer', label: 'The dealer', rentierAmount: toBigOrNull(state?.rentierProposal) },
    { party: 'platform', label: 'GAC', rentierAmount: toBigOrNull(state?.platformProposal) },
  ];

  return {
    isDisputed,
    supportsProposals,
    remainingDeposit: toBigOrNull(state?.remainingDeposit) ?? 0n,
    proposals,
    canPropose: isDisputed && supportsProposals,
    agreeable:
      isDisputed && supportsProposals
        ? proposals
            .filter(
              (proposal): proposal is SplitProposal & { rentierAmount: bigint } =>
                proposal.party !== 'you' && proposal.rentierAmount !== null
            )
            .map((proposal) => ({
              party: proposal.party as 'dealer' | 'platform',
              label: proposal.label,
              rentierAmount: proposal.rentierAmount,
            }))
        : [],
    canClaimStale:
      isDisputed &&
      supportsProposals &&
      availableAt !== null &&
      nowMs >= new Date(availableAt).getTime(),
    staleClaimAvailableAt: availableAt,
  };
};

/**
 * Everything a renter's own signature can change about their rental, as one comparable string.
 *
 * The progress panel stays up, and the buttons stay locked, until the deal actually differs from
 * what it was when the signature went out. The status alone is far too coarse here: a top-up
 * changes only the remaining deposit, an extension request only the pending period, and the first
 * proposal in a dispute only one of three numbers — none of them move the status at all.
 */
export const rentalDealFingerprint = (rental: ApiRentalDealItem): string => {
  const state = rental.rentalContractState;

  if (!state) {
    return `${rental.status}:no-contract`;
  }

  return [
    rental.status,
    state.address,
    state.isPaid,
    state.isReadyForPickup,
    state.isActive,
    state.isReturned,
    state.isInspection,
    state.isDefected,
    state.isDisputed,
    state.isCompleted,
    state.remainingDeposit,
    state.pendingExtensionPeriod,
    state.extensionTotal,
    state.rentalEnd,
    state.renterProposal ?? '',
    state.rentierProposal ?? '',
    state.platformProposal ?? '',
  ].join(':');
};

/** The renter's on-chain actions that are a single confirm-and-sign, with no amount to choose. */
export type RentalOnchainAction =
  | 'start'
  | 'return'
  | 'release'
  | 'cancel'
  | 'approveDefect'
  | 'disputeDefect'
  | 'claimStale'
  | 'agreeSplit';

export interface RentalContractCall {
  signature: string;
  args?: readonly (string | number | boolean)[];
}

/**
 * The contract call behind each action. `agreeSplit` carries the amount the renter is agreeing to
 * — proposing the same number as the other party is what executes the split, so "agree" is not a
 * separate function, it is the same `proposeSplit` with their number.
 */
export const rentalContractCall = (
  action: RentalOnchainAction,
  agreeAmount?: bigint
): RentalContractCall => {
  switch (action) {
    case 'start':
      return { signature: 'startRental()' };
    case 'return':
      return { signature: 'returnVehicle()' };
    case 'release':
      return { signature: 'releaseDeposit()' };
    case 'cancel':
      return { signature: 'cancel()' };
    case 'approveDefect':
      return { signature: 'approveDefect()' };
    case 'disputeDefect':
      return { signature: 'disputeDefect()' };
    case 'claimStale':
      return { signature: 'claimStaleDeposit()' };
    case 'agreeSplit':
      if (agreeAmount === undefined) {
        throw new Error('agreeSplit needs the amount being agreed to');
      }

      return { signature: 'proposeSplit(uint256)', args: [agreeAmount.toString()] };
    default: {
      // Exhaustiveness: a new action without a call fails the build rather than sending nothing.
      const never: never = action;

      throw new Error(`No contract call for rental action ${String(never)}`);
    }
  }
};
